import { flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import Checkbox from '@/components/UI/Form/Checkbox';
import ReactTableFooterNav from './ReactTableFooterNav';
import styles from './react-table.module.css';

interface IReactTableProps {
  cols: any;
  data: any;
  onRowClick?: (row: any) => void;
  fetching?: boolean;
  pagination?: {
    page: number;
    pageSize: number;
    pageCount: number;
    currentPage: number;
    setPageSize: (v: number) => void;
    nextPage: () => void;
    previousPage: () => void;
  };

  headerBorderAccent?: boolean;
  height?: string;
  onRowSelect?: (val: any[]) => void;
  tableClassName?: string;
  enableRowSelection?: boolean;
}

const ReactTable = ({
  cols,
  data,
  pagination,
  onRowClick,
  onRowSelect,
  fetching,
  headerBorderAccent,
  height = 'h-full',
  tableClassName = '',
  enableRowSelection = false,
}: IReactTableProps) => {
  const columns = useMemo(() => {
    if (enableRowSelection) {
      return [
        {
          id: 'rowSelect',
          maxSize: 10,
          header: (d: any) => (
            <Checkbox
              id='react-table-column-checkbox'
              name='ReactTableColumnCheckbox'
              checked={d.table.getIsAllRowsSelected()}
              onChange={d.table.getToggleAllRowsSelectedHandler()}
            />
          ),
          cell: (c: any) => (
            <Checkbox
              id='react-table-column-checkbox'
              name='ReactTableColumnCheckbox'
              checked={c.row.getIsSelected()}
              onChange={c.row.getToggleSelectedHandler()}
            />
          ),
        },
        ...cols,
      ];
    }
    return cols;
  }, [cols, enableRowSelection]);
  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data,
    columns,
    pageCount: pagination?.pageCount,
    state: {
      rowSelection,
    },
    manualPagination: true,
    enableColumnResizing: true,
    onRowSelectionChange: setRowSelection,
    enableRowSelection,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const selectedRows = table.getSelectedRowModel().flatRows;
  useEffect(() => {
    if (onRowSelect && selectedRows) {
      onRowSelect(selectedRows);
    }
  }, [selectedRows, onRowSelect]);

  return (
    <div className='overflow-auto'>
      <div className='relative w-full divide-y divide-gray-200 border overflow-hidden rounded-xl'>
        <table className={`w-full ${tableClassName}`}>
          <thead className={`bg-gray-50 ${headerBorderAccent && 'border-l-4'}`}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className='w-full'>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className='relative p-6 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'
                    colSpan={header.colSpan}
                    style={{
                      width: header.getSize(),
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}

                    <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      className={`${styles.resizer} ${header.column.getIsResizing() ? styles.isResizing : ''}`}
                    />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {fetching && (
            <tbody className='bg-white divide-y divide-gray-200 animate-pulse'>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <tr key={num}>
                  {Array.from(Array(columns.length).keys()).map((innerNum) => (
                    <td className='p-6' key={innerNum}>
                      <span className='h-2 rounded-md bg-neutralColor-light w-32 block' />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
          {!fetching && (
            <tbody className='bg-white divide-y divide-gray-200'>
              {table.getRowModel().rows.map((row) => {
                return (
                  <tr key={row.id} className='hover:bg-green-50 cursor-pointer'>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className='px-6 py-4 whitespace-nowrap'
                        style={{
                          width: cell.column.getSize(),
                        }}
                        onClick={() =>
                          onRowClick && cell.column.id !== 'rowSelect' && cell.column.id !== 'actions' && onRowClick(row.original)
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
        <div>
          {pagination && (
            <ReactTableFooterNav
              nextPage={pagination.nextPage}
              previousPage={pagination.previousPage}
              currentPage={pagination.currentPage}
              totalPage={table.getPageCount() || 1}
              perPage={pagination.pageSize}
              perPageHandler={pagination.setPageSize}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ReactTable;
